
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { 
  FiVideo, 
  FiVideoOff, 
  FiMonitor, 
  FiX, 
  FiLoader, 
  FiPlay, 
  FiAlertCircle, 
  FiEdit3, 
  FiMaximize, 
  FiMinimize 
} from "react-icons/fi";

export default function MainStage({
  zoomed, setZoomed, mediaStream, videoRef, screenRef, zoomedVideoRef,
  participants, activeScreenShare, viewerScreenShare, showWhiteboard, whiteboardSessionInfo,
  StreamerWhiteboard, ScreenAnnotationWhiteboard, handleCloseWhiteboard, appViewerScreenShare,
  isPlaying, isLoading, isInitializing, showPlayButton, mediaError, videoEnabled,
  handlePlayClick, retryCamera, renderSidebarToggleButton,
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const stageRef = useRef(null);
  const [boxStyle, setBoxStyle] = useState({ 
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    width: '100%', height: '100%' 
  });

  useEffect(() => {
    const updateBox = () => {
      if (!stageRef.current) return;
      const { clientWidth: w, clientHeight: h } = stageRef.current;
      if (!w || !h) return;
      
      let newBoxStyle = { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
      if (w / h > 16 / 9) {
        newBoxStyle.width = `${h * (16 / 9)}px`;
        newBoxStyle.height = `${h}px`;
      } else {
        newBoxStyle.width = `${w}px`;
        newBoxStyle.height = `${w * (9 / 16)}px`;
      }
      setBoxStyle(newBoxStyle);
    };

    updateBox();
    window.addEventListener("resize", updateBox);
    const observer = new ResizeObserver(updateBox);
    if (stageRef.current) observer.observe(stageRef.current);
    
    return () => {
      window.removeEventListener("resize", updateBox);
      observer.disconnect();
    };
  }, [zoomed, isFullscreen]);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = useCallback((e) => {
    e.stopPropagation();
    const el = document.querySelector(".main-video-container");
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen().catch((err) => console.log("Full screen error:", err));
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  const zoomLabel = useMemo(() => {
    if (!zoomed) return null;
    if (zoomed.type === "streamer") return { icon: <FiVideo className="h-4 w-4 text-blue-400" />, text: "Your Camera" };
    if (zoomed.type === "viewer") {
      const nm = participants?.find((p) => p.userId === zoomed.userId)?.name || "Viewer";
      return { icon: <FiVideo className="h-4 w-4 text-green-400" />, text: `${nm}'s Camera` };
    }
    if (zoomed.type === "screen") {
      const nm = activeScreenShare?.userName || "Streamer";
      return { icon: <FiMonitor className="h-4 w-4 text-purple-400" />, text: `${nm}'s Screen` };
    }
    if (zoomed.type === "viewer-screen") {
      const nm = viewerScreenShare?.userName || "Viewer";
      return { icon: <FiMonitor className="h-4 w-4 text-orange-400" />, text: `${nm}'s Screen` };
    }
    if (zoomed.type === "app-viewer-screen") {
      const nm = appViewerScreenShare?.userName || "App Viewer";
      return { icon: <FiMonitor className="h-4 w-4 text-pink-400" />, text: `${nm}'s App Screen` };
    }
    if (zoomed.type === "whiteboard") return { icon: <FiEdit3 className="h-4 w-4 text-yellow-300" />, text: "Whiteboard" };
    return null;
  }, [zoomed, participants, activeScreenShare, viewerScreenShare, appViewerScreenShare]);

  // ✅ UNIFIED MAIN STAGE VIDEO MANAGER WITH DEEP DEBUGGING
  useEffect(() => {
    if (videoRef.current) {
      const shouldPlayCamera = !zoomed || zoomed.type === "streamer";
      if (shouldPlayCamera && mediaStream) {
        if (videoRef.current.srcObject !== mediaStream) videoRef.current.srcObject = mediaStream;
        videoRef.current.play().catch(() => {});
      }
    }

    if (screenRef.current) {
      const shouldPlayScreen = zoomed?.type === "screen" && activeScreenShare?.stream;
      if (shouldPlayScreen) {
        if (screenRef.current.srcObject !== activeScreenShare.stream) screenRef.current.srcObject = activeScreenShare.stream;
        screenRef.current.muted = true;
        screenRef.current.play().catch(() => {});
      }
    }

    // 3. Zoomed Viewer Camera/Screen
    if (zoomedVideoRef.current) {
      const isViewerZoomed = zoomed && (zoomed.type === "viewer" || zoomed.type === "viewer-screen" || zoomed.type === "app-viewer-screen");
      const videoEl = zoomedVideoRef.current;

      if (isViewerZoomed && zoomed.stream) {
        const currentId = videoEl.dataset.streamId;
        
        if (currentId !== zoomed.stream.id) {
          videoEl.dataset.streamId = zoomed.stream.id;

          console.log("\n==============================================");
          console.log(`[DEBUG EDGE] 1. Modal item selected. Type: ${zoomed.type}`);
          
          // Debugging Tracks
          const tracks = zoomed.stream.getVideoTracks();
          console.log("[DEBUG EDGE] 2. Stream tracks Info:", tracks.map(t => ({
            id: t.id,
            readyState: t.readyState,
            enabled: t.enabled,
            muted: t.muted,
            kind: t.kind
          })));

          // Debugging Video Element Events
          const eventsToLog = ['play', 'playing', 'pause', 'waiting', 'stalled', 'suspend', 'abort', 'error', 'loadeddata', 'canplay'];
          const logEvent = (e) => {
            console.log(`[DEBUG EDGE] 🎬 Video Event Fired: -> ${e.type}`);
            if (e.type === 'error') {
              console.error("[DEBUG EDGE] ❌ VIDEO ERROR DETAILS:", videoEl.error);
            }
            if (e.type === 'stalled' || e.type === 'suspend') {
              console.warn(`[DEBUG EDGE] ⚠️ Browser Pipeline warning: ${e.type}. (Browser might have blocked rendering)`);
            }
          };

          // Attach listeners
          eventsToLog.forEach(evt => videoEl.addEventListener(evt, logEvent));

          setTimeout(() => {
            if (videoEl) {
              console.log("[DEBUG EDGE] 3. Assigning stream to video.srcObject");
              const freshStream = new MediaStream(zoomed.stream.getTracks());
              videoEl.srcObject = freshStream;
              
              console.log("[DEBUG EDGE] 4. Calling .load()");
              videoEl.load(); 
              
              console.log("[DEBUG EDGE] 5. Calling .play()");
              const playPromise = videoEl.play();
              if (playPromise !== undefined) {
                 playPromise.then(() => {
                   console.log("[DEBUG EDGE] ✅ .play() SUCCESS - Video should be playing now.");
                 }).catch(err => {
                   console.error(`[DEBUG EDGE] ❌ .play() FAILED: ${err.name} - ${err.message}`);
                 });
              }
            }
          }, 150);

          // Cleanup listeners when stream changes
          return () => {
            eventsToLog.forEach(evt => videoEl.removeEventListener(evt, logEvent));
          };
        }
      } else if (!isViewerZoomed && videoEl.srcObject) {
        videoEl.dataset.streamId = "";
        videoEl.srcObject = null;
        videoEl.load(); 
      }
    }
  }, [zoomed, mediaStream, activeScreenShare?.stream]);

  return (
    <div ref={stageRef} className="relative flex-1 bg-black overflow-hidden main-video-container flex items-center justify-center">
      
      {zoomed ? (
        <div style={boxStyle} className="bg-black overflow-hidden flex items-center justify-center">
          {zoomed.type === "whiteboard" ? (
            showWhiteboard && whiteboardSessionInfo ? (
              <StreamerWhiteboard
                key={`whiteboard-${whiteboardSessionInfo.sessionId}`}
                sessionId={whiteboardSessionInfo.sessionId}
                roomCode={whiteboardSessionInfo.roomCode}
                wsToken={whiteboardSessionInfo.wsToken}
                sessionInfo={whiteboardSessionInfo}
                isActive={showWhiteboard}
                onClose={handleCloseWhiteboard}
                isStreamer={true}
                allowViewersToDraw={true}
                mainScreenMode={true}
                compact={true}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                <div className="text-white text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-3" />
                  <p className="text-sm">Loading whiteboard...</p>
                </div>
              </div>
            )
          ) : (
            <>
              <video
                key={zoomed.stream?.id || zoomed.userId || zoomed.type}
                ref={
                  zoomed.type === "screen"
                    ? screenRef
                    : zoomed.type === "streamer"
                    ? videoRef
                    : zoomedVideoRef
                }
                autoPlay
                playsInline
                muted={true}
                className="absolute inset-0 w-full h-full object-contain"
                // 🔥 CSS GPU HACK ADDED HERE
                style={{ transform: "translateZ(0)", backfaceVisibility: "hidden", willChange: "transform" }}
                onError={(e) => console.error("Zoomed video error in UI:", e)}
              />

              {zoomLabel && (
                <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1 rounded-lg text-sm z-20 flex items-center space-x-2">
                  {zoomLabel.icon}
                  <span>{zoomLabel.text}</span>
                </div>
              )}

              {zoomed.type === "screen" && ScreenAnnotationWhiteboard && whiteboardSessionInfo && (
                <div className="absolute inset-0 z-30 pointer-events-none">
                  <div className="relative w-full h-full pointer-events-auto">
                    <ScreenAnnotationWhiteboard
                      key={`whiteboard-overlay-${whiteboardSessionInfo.sessionId}`}
                      sessionId={`${whiteboardSessionInfo.sessionId}-screen`} 
                      roomCode={whiteboardSessionInfo.roomCode}
                      wsToken={whiteboardSessionInfo.wsToken}
                      sessionInfo={whiteboardSessionInfo}
                      isActive={true}
                      isStreamer={true}
                      allowViewersToDraw={true}
                      compact={true}
                      onClose={() => {}}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div style={boxStyle} className="bg-black overflow-hidden flex items-center justify-center">
          <video
            key="default-cam"
            ref={videoRef}
            autoPlay
            playsInline
            muted={true}
            className={`absolute inset-0 w-full h-full object-contain bg-black ${
              zoomed?.type === "whiteboard" ? "invisible" : "visible"
            }`}
            // 🔥 CSS GPU HACK ADDED HERE
            style={{ transform: "translateZ(0)", backfaceVisibility: "hidden", willChange: "transform" }}
            onError={(e) => {
              console.error("Main video error:", e);
            }}
          />

          <div className="absolute top-4 left-4 flex items-center space-x-2 bg-black/70 text-white px-3 py-1.5 rounded-lg z-20">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium">LIVE</span>
          </div>

          {!videoEnabled && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-10">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3">
                  <FiVideoOff className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-white text-sm">Camera is off</p>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="absolute top-4 left-4 flex items-center space-x-4 z-10 pointer-events-none">
        <div className="flex items-center space-x-2 bg-black/70 text-sm text-white px-4 py-2 rounded-xl backdrop-blur-sm">
          <div className={`w-3 h-3 rounded-full ${isPlaying ? "bg-green-400 animate-pulse" : "bg-red-400"}`} />
          <span className="font-medium">{isPlaying ? "Live" : "Paused"}</span>
        </div>
      </div>

      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm z-30">
          <div className="text-center">
            <FiLoader className="h-12 w-12 text-blue-400 animate-spin mx-auto mb-4" />
            <p className="text-white font-medium text-lg">Loading camera...</p>
          </div>
        </div>
      )}
      {isInitializing && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm z-30">
          <div className="text-center">
            <FiLoader className="h-12 w-12 text-blue-400 animate-spin mx-auto mb-4" />
            <p className="text-white font-medium text-lg">Initializing stream...</p>
          </div>
        </div>
      )}

      {showPlayButton && !mediaError && !zoomed && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm cursor-pointer z-30"
          onClick={(e) => {
            e.stopPropagation();
            handlePlayClick?.();
          }}
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-full transition-all duration-300 transform hover:scale-110 shadow-2xl">
            <FiPlay className="h-16 w-16" />
          </div>
        </div>
      )}

      {mediaError && !zoomed && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/90 backdrop-blur-sm z-30">
          <div className="text-center p-8 bg-gray-800/80 rounded-2xl max-w-md backdrop-blur-sm">
            <FiAlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
            <p className="text-xl mb-2 font-semibold text-white">Camera not available</p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                retryCamera?.();
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg"
            >
              Retry Camera
            </button>
          </div>
        </div>
      )}

      <div className="absolute top-4 right-16 z-50 pointer-events-auto">
        <button
          onClick={toggleFullscreen}
          className="bg-gray-800/80 hover:bg-gray-700/80 p-3 rounded-lg shadow-lg transition-colors flex items-center justify-center"
          title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
        >
          {isFullscreen ? <FiMinimize className="h-5 w-5 text-white" /> : <FiMaximize className="h-5 w-5 text-white" />}
        </button>
      </div>

      <div className="absolute right-4 top-4 z-[60] pointer-events-auto">
        {renderSidebarToggleButton?.()}
      </div>
    </div>
  );
}